import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { saFetch, SALoading, fmtDate, inputCls, btnOutline, btnPrimary } from "./saCommon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, PencilSimple, Prohibit, CircleNotch } from "@phosphor-icons/react";

const EMPTY = {
  name: "", type: "percent", value: "", scope: "all", tenant_ids: [], code: "",
  valid_from: "", valid_until: "", applies_to: "both", single_use: false, max_uses: 0, active: true,
};

const SCOPE_LABELS = { all: "Todas las empresas", company: "Empresas específicas", companies: "Empresas específicas", code: "Código promocional" };
const AUDIENCE_CYCLE = { both: "Mensual y anual", monthly: "Solo mensual", annual: "Solo anual" };

export default function SADescuentos() {
  const [items, setItems] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    saFetch("/superadmin/discounts").then(setItems).catch((e) => toast.error(e.message));
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    saFetch("/superadmin/companies-v2?per_page=200").then((d) => setCompanies(d.items || [])).catch(() => {});
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name || "", type: d.type || "percent", value: d.value, scope: d.scope || "all",
      tenant_ids: d.tenant_ids || [], code: d.code || "", valid_from: (d.valid_from || "").slice(0, 10),
      valid_until: (d.valid_until || "").slice(0, 10), applies_to: d.applies_to || "both",
      single_use: !!d.single_use, max_uses: d.max_uses || 0, active: d.active !== false,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      value: Number(form.value) || 0,
      max_uses: Number(form.max_uses) || 0,
      scope: form.scope === "companies" ? "companies" : form.scope,
      valid_from: form.valid_from || "",
      valid_until: form.valid_until || "",
    };
    try {
      if (editing) {
        await saFetch(`/superadmin/discounts/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        toast.success("Descuento actualizado");
      } else {
        await saFetch("/superadmin/discounts", { method: "POST", body: JSON.stringify(payload) });
        toast.success("Descuento creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (d) => {
    try {
      await saFetch(`/superadmin/discounts/${d.id}`, { method: "DELETE" });
      toast.success("Descuento desactivado");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggleTenant = (id) =>
    setForm((p) => ({ ...p, tenant_ids: p.tenant_ids.includes(id) ? p.tenant_ids.filter((x) => x !== id) : [...p.tenant_ids, id] }));

  if (!items) return <SALoading label="Cargando descuentos..." />;

  return (
    <div data-testid="sa-descuentos">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Descuentos</h1>
          <p className="text-sm text-muted-foreground mt-1">Cupones y promociones aplicables a las suscripciones</p>
        </div>
        <Button className={btnPrimary} data-testid="sa-new-discount" onClick={openCreate}>
          <Plus className="mr-2" size={15} /> Nuevo descuento
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="border border-border rounded-sm p-10 text-center text-muted-foreground bg-card" data-testid="sa-discounts-empty">
          Aún no hay descuentos. Crea el primero para promocionar tus planes.
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto bg-card">
          <table className="w-full text-sm min-w-[860px]" data-testid="sa-discounts-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="p-3 font-normal">Nombre / Código</th>
                <th className="p-3 font-normal">Valor</th>
                <th className="p-3 font-normal">Alcance</th>
                <th className="p-3 font-normal">Vigencia</th>
                <th className="p-3 font-normal">Usos</th>
                <th className="p-3 font-normal">Estado</th>
                <th className="p-3 font-normal text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((d) => (
                <tr key={d.id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`sa-discount-row-${d.id}`}>
                  <td className="p-3">
                    <p className="text-foreground font-medium">{d.name || d.code || "Sin nombre"}</p>
                    {d.code && <p className="text-xs text-primary font-mono">{d.code}</p>}
                  </td>
                  <td className="p-3 text-foreground/80">{d.type === "percent" ? `${d.value}%` : `L ${Number(d.value || 0).toFixed(2)}`}</td>
                  <td className="p-3">
                    <p className="text-foreground/80 text-xs">{SCOPE_LABELS[d.scope] || d.scope}</p>
                    <p className="text-[11px] text-muted-foreground">{AUDIENCE_CYCLE[d.applies_to] || "Ambos ciclos"}</p>
                  </td>
                  <td className="p-3 text-foreground/80 text-xs">
                    {d.valid_from || d.valid_until ? `${fmtDate(d.valid_from)} → ${fmtDate(d.valid_until)}` : "Sin límite"}
                  </td>
                  <td className="p-3 text-foreground/80">{d.used_count || 0}{d.max_uses ? ` / ${d.max_uses}` : ""}</td>
                  <td className="p-3">
                    <Badge variant="outline" className={`rounded-sm font-normal ${d.active !== false ? "border-emerald-500/40 text-emerald-500" : "border-border text-muted-foreground"}`}>
                      {d.active !== false ? "Activo" : "Inactivo"}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground hover:bg-accent" data-testid={`sa-discount-edit-${d.id}`} onClick={() => openEdit(d)}>
                        <PencilSimple className="mr-1" size={14} /> Editar
                      </Button>
                      {d.active !== false && (
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-950/40" data-testid={`sa-discount-deactivate-${d.id}`} onClick={() => deactivate(d)}>
                          <Prohibit className="mr-1" size={14} /> Desactivar
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">{editing ? "Editar descuento" : "Nuevo descuento"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="sa-discount-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nombre</Label>
              <Input className={inputCls} data-testid="sa-discount-name" placeholder="Ej. Promo lanzamiento" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className={inputCls} data-testid="sa-discount-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="percent">Porcentaje (%)</SelectItem>
                  <SelectItem value="fixed">Monto fijo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Valor *</Label>
              <Input type="number" step="0.01" min="0" required className={inputCls} data-testid="sa-discount-value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Alcance</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger className={inputCls} data-testid="sa-discount-scope"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">Todas las empresas</SelectItem>
                  <SelectItem value="companies">Empresas específicas</SelectItem>
                  <SelectItem value="code">Código promocional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.scope === "code" && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Código promocional *</Label>
                <Input className={`${inputCls} font-mono uppercase`} data-testid="sa-discount-code" placeholder="EJ. LANZA2026" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
              </div>
            )}
            {form.scope === "companies" && (
              <div className="sm:col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Empresas ({form.tenant_ids.length})</Label>
                <div className="border border-border rounded-sm max-h-40 overflow-y-auto divide-y divide-border">
                  {companies.map((c) => (
                    <label key={c.tenant_id} className="flex items-center gap-2 p-2.5 text-sm text-foreground/80 cursor-pointer hover:bg-muted/50">
                      <Checkbox checked={form.tenant_ids.includes(c.tenant_id)} onCheckedChange={() => toggleTenant(c.tenant_id)} className="border-input" data-testid={`sa-discount-tenant-${c.tenant_id}`} />
                      {c.name || c.tenant_id.slice(0, 8)}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Válido desde</Label>
              <Input type="date" className={inputCls} data-testid="sa-discount-from" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Válido hasta</Label>
              <Input type="date" className={inputCls} data-testid="sa-discount-until" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Aplica a</Label>
              <Select value={form.applies_to} onValueChange={(v) => setForm({ ...form, applies_to: v })}>
                <SelectTrigger className={inputCls} data-testid="sa-discount-applies"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="both">Mensual y anual</SelectItem>
                  <SelectItem value="monthly">Solo mensual</SelectItem>
                  <SelectItem value="annual">Solo anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Máx. usos (0 = ilimitado)</Label>
              <Input type="number" min="0" className={inputCls} data-testid="sa-discount-max-uses" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="sa-discount-active" /> Descuento activo
              </label>
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="submit" disabled={saving} className={btnPrimary} data-testid="sa-discount-save">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />} {editing ? "Guardar cambios" : "Crear descuento"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
